import React, { useState, useMemo } from 'react';
import { X, Printer, Save, CheckCircle, Sparkles, Activity } from 'lucide-react';
import { sections as bodySections, deeperDive as bodyDeeperDive, scaleOptions as bodyScale } from '../data/bodyAssessment';
import { assessmentLibrary } from '../data/assessmentLibrary';
import { buildSectionScores, quizHighLoadBand } from '../data/quizMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QUIZ_TITLES = {
  wellness_pattern_assessment: 'What Is Your Body Asking For? — A Wellness Pattern Assessment',
  lifestyle_assessment: 'The Vitality Potential Tool — Lifestyle Load & Resilience',
  'lifestyle-assessment': 'The Vitality Potential Tool — Lifestyle Load & Resilience',
  metabolic_rhythm: 'Metabolic Rhythm Quiz',
};

const sectionMeta = (name) => bodySections.find((s) => s.name === name);
const bandColor = (b) =>
  b === 'High Load' || b === 'Dysregulated' ? 'bg-rose-100 text-rose-700'
    : b === 'Moderate Load' || b === 'Compensating' ? 'bg-orange-100 text-orange-700'
    : b === 'Mild Load' ? 'bg-amber-100 text-amber-700'
    : b === 'Resilient' ? 'bg-emerald-100 text-emerald-700'
    : 'bg-stone-100 text-stone-600';

const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const PractitionerWorksheet = ({ result, adminEmail, adminPassword, onClose, onSaved }) => {
  const ranked = useMemo(() => buildSectionScores(result), [result]);
  const quizTitle = QUIZ_TITLES[result.quiz_type] || 'Assessment';
  const highBand = quizHighLoadBand[result.quiz_type];

  const existing = result.practitioner_notes || {};
  const [systemNotes, setSystemNotes] = useState(existing.systemNotes || {});
  const [recommendations, setRecommendations] = useState(existing.recommendations || '');
  const [followUp, setFollowUp] = useState(existing.followUp || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const reflection = result.answers?.reflection;
  const deeper = result.answers?.deeperDive;
  const bodyScaleLabel = (v) => (bodyScale.find((o) => o.value === v)?.label ?? '');
  const deeperDetail = useMemo(() => {
    if (result.quiz_type !== 'wellness_pattern_assessment') return [];
    const responses = result.answers?.responses || {};
    return bodyDeeperDive.questions.map((qText, qi) => {
      const flat = bodySections.length * 5 + qi;
      const val = responses[flat];
      return { qText, val, label: val !== undefined ? `${bodyScaleLabel(val)} (${val})` : '—' };
    });
  }, [result]);
  const dateStr = result.created_at ? new Date(result.created_at).toLocaleDateString() : '';
  const highLoad = highBand ? ranked.filter((r) => r.band === highBand) : [];

  const setNote = (name, val) => setSystemNotes((prev) => ({ ...prev, [name]: val }));

  const insertSnippet = (name, snippet) => {
    setSystemNotes((prev) => {
      const cur = prev[name] || '';
      const sep = cur.trim() ? '\n\n' : '';
      return { ...prev, [name]: cur + sep + snippet.body };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/quiz-results/${result.quiz_id}/notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_email: adminEmail,
          admin_password: adminPassword,
          notes: { systemNotes, recommendations, followUp },
        }),
      });
      if (res.ok) {
        setSaved(true);
        onSaved && onSaved();
        setTimeout(() => setSaved(false), 2500);
      }
    } catch (e) {
      console.error('Error saving worksheet notes:', e);
    }
    setSaving(false);
  };

  const handlePrint = () => {
    const rows = ranked.map((r) => {
      const note = (systemNotes[r.name] || '').trim();
      const meta = sectionMeta(r.name);
      const support = meta ? meta.support.join(' · ') : '';
      return `<div style="margin-bottom:20px;page-break-inside:avoid;">
        <div style="display:flex;justify-content:space-between;border-bottom:1px solid #d9d4c7;padding-bottom:4px;">
          <strong>${esc(r.name)}</strong><span style="white-space:nowrap;">${r.score}/${r.max} · ${esc(r.band)}</span>
        </div>
        ${meta ? `<p style="color:#6b6b60;font-style:italic;margin:6px 0 2px;">${esc(meta.interpretation)}</p>` : ''}
        ${support ? `<p style="color:#9a9488;font-size:12px;margin:0 0 6px;">Potential support: ${esc(support)}</p>` : ''}
        <div style="white-space:pre-wrap;margin-top:6px;border:1px solid #eee;border-radius:6px;padding:10px;min-height:48px;">${note ? esc(note) : ''}</div>
      </div>`;
    }).join('');
    const html = `<html><head><title>Practitioner Worksheet — ${esc(result.email)}</title></head>
      <body style="font-family:Georgia,'Times New Roman',serif;max-width:740px;margin:28px auto;color:#2f3b30;line-height:1.55;padding:0 16px;">
      <h1 style="font-size:24px;margin-bottom:2px;">Practitioner Worksheet</h1>
      <p style="color:#6b6b60;margin-top:0;">${esc(quizTitle)}</p>
      <p>Client: <strong>${esc(result.email)}</strong> &nbsp;·&nbsp; Completed: ${esc(dateStr)}</p>
      ${highLoad.length ? `<p style="background:#fce8e6;border-radius:8px;padding:8px 12px;">Primary (High Load): <strong>${highLoad.map((h) => esc(h.name)).join(', ')}</strong></p>` : ''}
      <hr style="border:none;border-top:2px solid #2f3b30;margin:14px 0;"/>
      <h2 style="font-size:17px;">Systems — Highest to Lowest Load</h2>
      ${rows}
      <h2 style="font-size:17px;">Recommendations</h2>
      <div style="white-space:pre-wrap;min-height:80px;border:1px solid #eee;border-radius:6px;padding:10px;">${esc(recommendations)}</div>
      <h2 style="font-size:17px;">Follow-up</h2>
      <div style="white-space:pre-wrap;min-height:80px;border:1px solid #eee;border-radius:6px;padding:10px;">${esc(followUp)}</div>
      ${reflection ? `<p style="margin-top:16px;"><em>Client's final reflection: "${esc(reflection)}"</em></p>` : ''}
      ${deeper && deeper.answered ? `<p>Deeper-dive signal: ${deeper.score}/${deeper.max}</p>` : ''}
      ${deeperDetail.length ? `<h3 style="margin-top:18px;">${esc(bodyDeeperDive.name)}</h3><table style="width:100%;border-collapse:collapse;font-size:13px;">${deeperDetail.map((d) => `<tr><td style="padding:4px 8px;border-bottom:1px solid #eee;color:#444;">${esc(d.qText)}</td><td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:right;white-space:nowrap;font-weight:600;">${esc(d.label)}</td></tr>`).join('')}</table>` : ''}
      <p style="color:#9a9488;font-size:11px;margin-top:24px;">Grounded Wellness with Jill · For practitioner use.</p>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 350);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="practitioner-worksheet">
      <div className="bg-cream rounded-2xl w-full max-w-3xl my-6 shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-forest text-cream rounded-t-2xl px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-heading text-xl">Practitioner Worksheet</h2>
            <p className="font-body text-cream/70 text-sm">{result.email} · {dateStr}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handlePrint} className="inline-flex items-center gap-1.5 bg-cream/15 hover:bg-cream/25 px-3 py-2 rounded-full text-sm font-body transition-colors" data-testid="worksheet-print-btn">
              <Printer size={15} /> Print / PDF
            </button>
            <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-1.5 bg-terracotta hover:bg-terracotta-dark px-3 py-2 rounded-full text-sm font-body transition-colors disabled:opacity-60" data-testid="worksheet-save-btn">
              {saved ? <CheckCircle size={15} /> : <Save size={15} />} {saved ? 'Saved' : saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={onClose} className="p-2 hover:bg-cream/15 rounded-full transition-colors" data-testid="worksheet-close-btn"><X size={18} /></button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* High-load highlight */}
          {highLoad.length > 0 && (
            <div className="bg-rose-50 border border-rose-100 rounded-xl p-4">
              <span className="inline-flex items-center gap-2 font-body font-medium text-rose-700 text-sm">
                <Sparkles size={15} /> Primary (High Load): {highLoad.map((h) => h.name).join(', ')}
              </span>
            </div>
          )}

          {/* Reflection + deeper-dive */}
          <div className="grid sm:grid-cols-2 gap-3">
            {reflection && (
              <div className="bg-white rounded-xl p-4 border border-stone-100">
                <p className="text-xs font-body text-stone-400 uppercase tracking-wide mb-1">Final reflection</p>
                <p className="font-body text-stone-700 text-sm italic">"{reflection}"</p>
              </div>
            )}
            {deeper && deeper.answered && (
              <div className="bg-white rounded-xl p-4 border border-stone-100">
                <p className="text-xs font-body text-stone-400 uppercase tracking-wide mb-1 inline-flex items-center gap-1"><Activity size={12} /> Deeper-dive signal</p>
                <p className="font-body text-stone-700 text-sm">{deeper.score}/{deeper.max}</p>
              </div>
            )}
          </div>

          {/* Deeper-dive detail (Body assessment only) */}
          {deeperDetail.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-testid="worksheet-deeper-dive">
              <p className="text-xs font-body text-slate-500 uppercase tracking-wide mb-2 inline-flex items-center gap-1">
                <Activity size={12} /> {bodyDeeperDive.name}{deeper && deeper.answered ? ` · ${deeper.score}/${deeper.max}` : ''}
              </p>
              <div className="space-y-1.5">
                {deeperDetail.map((d, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 bg-white rounded-lg px-3 py-2 border border-stone-100">
                    <span className="text-sm font-body text-stone-600">{d.qText}</span>
                    <span className="text-sm font-body font-medium text-forest text-right flex-shrink-0 whitespace-nowrap">{d.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-system notes */}
          <div>
            <h3 className="font-heading text-lg text-forest mb-1">Systems — Highest to Lowest Load</h3>
            <p className="font-body text-stone-500 text-sm mb-4">Paste ailments, remedies, and notes under each system below.</p>
            <div className="space-y-4">
              {ranked.map((r) => {
                const meta = sectionMeta(r.name);
                const snippets = (meta && assessmentLibrary[meta.key]) || [];
                return (
                  <div key={r.name} className="bg-white rounded-xl p-4 border border-stone-100" data-testid={`worksheet-system-${r.name}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-body font-medium text-forest">{r.name}</span>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-body font-medium ${bandColor(r.band)}`}>{r.band} · {r.score}/{r.max}</span>
                    </div>
                    {meta && <p className="font-body text-stone-500 text-sm italic mb-1">{meta.interpretation}</p>}
                    {meta && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {meta.support.map((sup) => (
                          <span key={sup} className="px-2 py-0.5 bg-stone-100 rounded-full text-xs font-body text-stone-500">{sup}</span>
                        ))}
                      </div>
                    )}
                    {snippets.length > 0 && (
                      <div className="mb-2 bg-forest/5 rounded-lg p-3 border border-forest/10">
                        <p className="text-xs font-body text-forest font-medium mb-1.5">Insert from library:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {snippets.map((sn) => (
                            <button
                              key={sn.id}
                              type="button"
                              onClick={() => insertSnippet(r.name, sn)}
                              className="px-2.5 py-1 rounded-full text-xs font-body bg-forest/10 text-forest hover:bg-forest/20 transition-colors"
                              data-testid={`snippet-${sn.id}`}
                            >
                              + {sn.title}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <textarea
                      value={systemNotes[r.name] || ''}
                      onChange={(e) => setNote(r.name, e.target.value)}
                      placeholder="Paste ailments / remedies / notes for this system…"
                      rows={3}
                      className="w-full px-3 py-2 rounded-lg border border-stone-200 font-body text-sm text-stone-700 focus:outline-none focus:border-forest resize-y"
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* General sections */}
          <div className="bg-white rounded-xl p-4 border border-stone-100">
            <label className="font-heading text-base text-forest block mb-2">Recommendations <span className="font-body text-[11px] text-stone-400 font-normal">· client sees this once results are shared in their journal</span></label>
            <textarea
              value={recommendations}
              onChange={(e) => setRecommendations(e.target.value)}
              placeholder="Overall recommendations, programs, protocols…"
              rows={4}
              className="w-full px-3 py-2 rounded-lg border border-stone-200 font-body text-sm text-stone-700 focus:outline-none focus:border-forest resize-y"
            />
          </div>
          <div className="bg-white rounded-xl p-4 border border-stone-100">
            <label className="font-heading text-base text-forest block mb-2">Follow-up <span className="font-body text-[11px] text-stone-400 font-normal">· shown to the client as “Next steps”</span></label>
            <textarea
              value={followUp}
              onChange={(e) => setFollowUp(e.target.value)}
              placeholder="Next check-in, action items, things to revisit…"
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-stone-200 font-body text-sm text-stone-700 focus:outline-none focus:border-forest resize-y"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={handlePrint} className="inline-flex items-center gap-1.5 border border-stone-300 text-forest px-4 py-2 rounded-full text-sm font-body hover:bg-stone-50 transition-colors">
              <Printer size={15} /> Print / Save as PDF
            </button>
            <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-1.5 bg-forest text-cream px-5 py-2 rounded-full text-sm font-body hover:bg-forest-dark transition-colors disabled:opacity-60">
              {saved ? <CheckCircle size={15} /> : <Save size={15} />} {saved ? 'Saved' : saving ? 'Saving…' : 'Save Notes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PractitionerWorksheet;
